import { Text, View, Svg, Polygon, Page } from '@react-pdf/renderer';

import { PublicEventDetailsResponse } from '@hubs101/booking-api-skd-client/lib/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { globalStyles } from '../../styles';
import { styles } from './styles';
import { truncateText, normalizeText } from '../../../utils';

const ConnectorArrow = ({
  side,
  color,
}: {
  side: 'leftColumn' | 'rightColumn';
  color: string;
}) => {
  const isLeftColumn = side === 'leftColumn';

  return (
    <View
      style={{
        position: 'absolute',
        top: 85,
        ...(isLeftColumn ? { right: -22 } : { left: -22 }),
        flexDirection: isLeftColumn ? 'row' : 'row-reverse',
        alignItems: 'center',
      }}
    >
      {/* Line */}
      <View
        style={{
          width: 10.5,
          height: 2,
          backgroundColor: color,
        }}
      />

      {/* Arrowhead (SVG triangle) */}
      <Svg width={11} height={11}>
        <Polygon
          points={isLeftColumn ? '0,0 11,5.5 0,11' : '11,0 0,5.5 11,11'}
          style={{ fill: color }}
        />
      </Svg>
    </View>
  );
};

interface KeyTopicsPageProps {
  event: PublicEventDetailsResponse;
  header: JSX.Element;
  footer: JSX.Element;
}

const KeyTopicsPage = ({ event, header, footer }: KeyTopicsPageProps) => {
  const { t } = useTranslation();

  const { leftColumn, rightColumn } = useMemo(() => {
    let items =
      event?.website_content?.keytopics
        ?.filter(e => e.published)
        ?.sort((a, b) => a.order - b.order)
        ?.slice(0, 8) // max 8 real items
        ?.map(keyTopic => ({
          title: keyTopic.title,
          subtitle: keyTopic.description,
          order: keyTopic.order,
        })) ?? [];

    // pad with empty items if less than 8
    const emptyCount = 8 - items.length;
    if (emptyCount > 0) {
      items = [
        ...items,
        ...Array.from({ length: emptyCount }).map((_, i) => ({
          title: '',
          subtitle: '',
          order: items.length + i,
        })),
      ];
    }

    return {
      leftColumn: items.filter((_, index) => index % 2 === 0),
      rightColumn: items.filter((_, index) => index % 2 === 1),
    };
  }, [event?.website_content?.keytopics]);

  return (
    <Page style={styles.page}>
      {header}

      <View style={styles.contentWrapper}>
        <Text
          style={{
            ...styles.title,
            color: event?.design?.themeColor ?? '#27343d',
          }}
        >
          {t('key-topics-to-discuss', {
            year: new Date(event.start_date).getFullYear(),
          })}
        </Text>
        <View
          style={{
            ...globalStyles.lineBar,
            borderColor: event?.design?.themeColor,
            marginLeft: 0,
            marginBottom: 10,
            width: 89,
          }}
        />

        <View style={styles.columnsWrapper}>
          <View style={[styles.column, { marginRight: 10 }]}>
            {leftColumn.map((item: any, index: number) => {
              const hideArrow =
                leftColumn.length > rightColumn.length &&
                index === leftColumn.length - 1;

              return (
                <View
                  wrap={false}
                  style={{
                    ...styles.gridItem,
                    borderColor: event?.design?.themeColor ?? 'black',
                    position: 'relative',
                  }}
                  key={item.order}
                >
                  {!hideArrow && (
                    <ConnectorArrow
                      side="leftColumn"
                      color={event?.design?.themeColor ?? 'black'}
                    />
                  )}

                  <Text
                    style={{
                      ...styles.itemTitle,
                      color: event?.design?.themeColor ?? 'black',
                    }}
                  >
                    {truncateText(normalizeText(item.title), 100)}
                  </Text>
                  <Text style={styles.itemText}>
                    {truncateText(normalizeText(item.subtitle), 300)}
                  </Text>
                </View>
              );
            })}
          </View>

          <View style={[styles.column, { marginLeft: 10, marginTop: 75 }]}>
            {rightColumn.map((item: any, index: number) => {
              const hideArrow =
                leftColumn.length === rightColumn.length &&
                index === rightColumn.length - 1;

              return (
                <View
                  wrap={false}
                  style={{
                    ...styles.gridItem,
                    borderColor: event?.design?.themeColor ?? 'black',
                    position: 'relative',
                  }}
                  key={item.order}
                >
                  {!hideArrow && (
                    <ConnectorArrow
                      side="rightColumn"
                      color={event?.design?.themeColor ?? 'black'}
                    />
                  )}

                  <Text
                    style={{
                      ...styles.itemTitle,
                      color: event?.design?.themeColor ?? 'black',
                    }}
                  >
                    {truncateText(normalizeText(item.title), 100)}
                  </Text>
                  <Text style={styles.itemText}>
                    {truncateText(normalizeText(item.subtitle), 300)}
                  </Text>
                </View>
              );
            })}
          </View>
        </View>
      </View>

      {footer}
    </Page>
  );
};

export default KeyTopicsPage;
